/**
 * /attempts/:id/result — Public Result page (Attempt Report).
 *
 * Binding contract: `docs/ia/public-result.md`.
 *
 * Substrate (15-FG/32): **NOT inside <Shell>** — public credential surface.
 * Composes its own minimal chrome (wordmark header in full mode; suppressed
 * in embed mode). Composition uses `@/components/ui` primitives only.
 *
 * Modes:
 *   - Full mode (`/attempts/:id/result`)  → wordmark header on top, score
 *     hero → review → certificate.
 *   - Embed mode (`/attempts/:id/result/embed`) → header suppressed, page
 *     blends into host (bg shifts to `bg-card`). Cert box keeps its accent
 *     band so the credential remains identifiable per Polish 10.
 *
 * The not-found state renders prose only — never a "Back to dashboard" link
 * (this is a public surface; the candidate may not be authed).
 *
 * Cross-cutting invariants:
 *   - Inv 12 (no console.log) — none introduced.
 *   - Inv 13 (no `any` / @ts-ignore) — none.
 *   - Inv 14 (internal nav via <Link>) — Link primitive used for any
 *     internal navigation; the wordmark is a Link to "/".
 *   - Inv 15 (atoms from barrel) — Heading / Eyebrow / Button / Card /
 *     Badge / Link composed from `@/components/ui`.
 */
import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  getAttemptResult,
  getBenchmarkComparison,
  type AttemptResultResponse,
  type BenchmarkComparison,
} from '../../state/analytics/api.ts';
import { Badge, Button, Card, Eyebrow, Heading, Link } from '@/components/ui';

const API_BASE = '';

export interface ResultPageProps {
  /** Embed mode suppresses chrome (wordmark header). Default false. */
  embed?: boolean;
}

type LoadError = {
  code: string;
  message: string;
};

type OptionReviewStatus = 'chosen-correct' | 'chosen-wrong' | 'correct-not-chosen' | 'neither';

interface OptionReviewRow {
  id: string;
  text: string;
  status: OptionReviewStatus;
}

function formatDuration(totalSeconds: number): string {
  const safeSeconds = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function toLocaleTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
}

function buildOptionReviewRows(
  question: AttemptResultResponse['per_question'][number],
): OptionReviewRow[] {
  const options = [...(question.options ?? [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );
  if (options.length === 0) {
    return [];
  }

  const selectedIds = new Set(
    question.type === 'multi'
      ? question.user_answer.split(',').map((item) => item.trim()).filter((item) => item.length > 0)
      : [question.user_answer.trim()].filter((item) => item.length > 0),
  );

  return options.map((option) => {
    const selected = selectedIds.has(option.id);
    const correct = option.is_correct;

    let status: OptionReviewStatus = 'neither';
    if (selected && correct) {
      status = 'chosen-correct';
    } else if (selected && !correct) {
      status = 'chosen-wrong';
    } else if (!selected && correct) {
      status = 'correct-not-chosen';
    }

    return {
      id: option.id,
      text: option.text,
      status,
    };
  });
}

function optionStatusLabel(status: OptionReviewStatus): string {
  switch (status) {
    case 'chosen-correct':
      return '✓ chosen-correct';
    case 'chosen-wrong':
      return '✗ chosen-wrong';
    case 'correct-not-chosen':
      return '⊙ correct-not-chosen';
    case 'neither':
      return '· neither';
  }
}

function optionStatusStyles(status: OptionReviewStatus): { backgroundColor: string; color: string } {
  switch (status) {
    case 'chosen-correct':
      return { backgroundColor: 'var(--bg-success)', color: 'var(--success)' };
    case 'chosen-wrong':
      return { backgroundColor: 'rgba(185, 28, 28, 0.12)', color: 'var(--error)' };
    case 'correct-not-chosen':
      return { backgroundColor: 'rgba(1, 57, 183, 0.1)', color: 'var(--accent)' };
    case 'neither':
      return { backgroundColor: 'var(--bg-warm)', color: 'var(--muted)' };
  }
}

// ─── Minimal public chrome (NOT Shell) ────────────────────────────────────────

/**
 * Wordmark header for the public result surface in full mode.
 *
 * Renders a left-aligned "BIMAssess" wordmark linking to "/" (catalogue
 * fallback) — the only internal nav the public credential page exposes.
 * No catalogue / dashboard / data nav, no user menu — this is deliberately
 * not the authed-app shell.
 *
 * In embed mode this component is not rendered at all (Polish 10 — the
 * cert box's accent band remains the credential signal).
 */
function PublicResultChrome(): ReactElement {
  return (
    <header
      role="banner"
      className="border-b border-line bg-card"
    >
      <div className="mx-auto flex max-w-page items-center justify-between px-6 py-5">
        <Link
          to="/"
          aria-label="BIMAssess"
          className="font-sans text-lg font-medium tracking-tight text-ink no-underline hover:text-accent"
        >
          BIMAssess
        </Link>
      </div>
    </header>
  );
}

interface PageFrameProps {
  embed: boolean;
  children: ReactNode;
}

/**
 * Page-level frame for the public result surface.
 *
 * Full mode renders the wordmark header + cream page background; embed
 * mode renders no header and shifts the background to `bg-card` (white)
 * to blend into a host page (Polish 10).
 */
function PageFrame({ embed, children }: PageFrameProps): ReactElement {
  const bg = embed ? 'bg-card' : 'bg-bg';
  return (
    <div className={`min-h-screen ${bg}`}>
      {embed ? null : <PublicResultChrome />}
      <div className="mx-auto max-w-page px-6 py-10">
        <main role="main">{children}</main>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ResultPage({ embed = false }: ResultPageProps = {}): ReactElement {
  const params = useParams<{ id: string }>();
  const attemptId = params.id ?? '';

  const [result, setResult] = useState<AttemptResultResponse | null>(null);
  const [benchmark, setBenchmark] = useState<BenchmarkComparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LoadError | null>(null);

  async function loadResult(): Promise<void> {
    setLoading(true);
    setError(null);

    try {
      const res = await getAttemptResult(API_BASE, attemptId);
      if (!res.ok) {
        setError({ code: res.error.code, message: res.error.message });
        setResult(null);
        return;
      }

      setResult(res.data);

      const benchRes = await getBenchmarkComparison(API_BASE, attemptId);
      if (benchRes.ok) {
        setBenchmark(benchRes.data);
      } else {
        setBenchmark(null);
      }
    } catch {
      setError({ code: 'network', message: 'Unable to load result. Please try again.' });
      setResult(null);
      setBenchmark(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadResult();
  }, [attemptId]);

  if (loading) {
    return (
      <PageFrame embed={embed}>
        <div className="space-y-6" aria-label="Loading result">
          <div className="h-28 animate-pulse rounded-lg bg-bg-warm" />
          <div className="h-20 animate-pulse rounded-lg bg-bg-warm" />
          <div className="h-20 animate-pulse rounded-lg bg-bg-warm" />
          <div className="h-64 animate-pulse rounded-lg bg-bg-warm" />
        </div>
      </PageFrame>
    );
  }

  if (error || !result) {
    if (error?.code === 'not_found') {
      // Public surface: NO link to /dashboard (the candidate may be
      // unauthenticated — `docs/ia/public-result.md` § Error & empty states).
      // Quiet prose only, editorial tone.
      return (
        <PageFrame embed={embed}>
          <Card variant="default" className="p-8" role="alert">
            <Heading level="h1" className="text-h2">
              Attempt not found
            </Heading>
            <p className="mt-3 text-body text-muted">
              The requested result does not exist or is no longer available. If you reached this page from a shared link, please check with the sender.
            </p>
          </Card>
        </PageFrame>
      );
    }

    return (
      <PageFrame embed={embed}>
        <Card variant="default" className="p-8" role="alert">
          <Heading level="h2" className="text-h3">
            We could not load this result
          </Heading>
          <p className="mt-3 text-body text-error">
            {error?.message ?? 'Unable to load result.'}
          </p>
          <div className="mt-5">
            <Button variant="secondary" type="button" onClick={() => void loadResult()}>
              Retry
            </Button>
          </div>
        </Card>
      </PageFrame>
    );
  }

  const attemptedAt = result.started_at ?? result.completed_at;
  const candidateName = result.candidate_name?.trim() || '—';
  const candidateEmail = result.candidate_email?.trim() || '—';

  return (
    <PageFrame embed={embed}>
      <div className="space-y-8">
        {/* Score hero — pass/fail Badge + score % + sub-line + test title + identity.
            Per `docs/ia/public-result.md` § Minimum 4-9. The score percentage is
            the credential moment; Space Mono numeric, colour-tinted by pass state. */}
        <section
          aria-labelledby="result-hero-heading"
          className="rounded-lg border border-line bg-card p-8 shadow-card"
        >
          <Eyebrow>Result</Eyebrow>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-sans text-small text-muted">Score</p>
              <Heading
                id="result-hero-heading"
                level="h1"
                className="font-mono text-5xl leading-none tracking-tight"
              >
                {result.score}%
              </Heading>
              <p className="mt-3 font-sans text-small text-muted">
                {result.correct_count} correct out of {result.total_questions} · Pass mark: {result.test.pass_mark}%
              </p>
            </div>
            <Badge variant={result.passed ? 'success' : 'error'}>
              {result.passed ? '✓ Passed' : '✗ Failed'}
            </Badge>
          </div>
          <p className="mt-4 font-sans text-body-lg font-medium text-ink">
            {result.test.title}
          </p>
          <p className="mt-1 font-sans text-small text-muted">
            {candidateName} · {candidateEmail}
          </p>
          {benchmark && benchmark.sample_size >= 50 && benchmark.percentile !== null && (
            <p className="mt-3 font-sans text-small text-muted">
              Percentile: higher than {benchmark.percentile}% of peers (n={benchmark.sample_size})
            </p>
          )}
        </section>

        {/* Candidate identity — kept as a separate region for the existing
            test expectations and for accessibility (named landmark). */}
        <section
          aria-labelledby="candidate-identity-heading"
          className="rounded-lg border border-line bg-card p-6"
        >
          <Heading id="candidate-identity-heading" level="h2" className="text-h3">
            Candidate identity
          </Heading>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted-2">Name</dt>
              <dd>{candidateName}</dd>
            </div>
            <div>
              <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted-2">Email</dt>
              <dd>{candidateEmail}</dd>
            </div>
            <div>
              <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted-2">Attempt started</dt>
              <dd>{toLocaleTimestamp(attemptedAt)}</dd>
            </div>
            <div>
              <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted-2">Duration</dt>
              <dd className="font-mono">{formatDuration(result.duration_seconds)}</dd>
            </div>
          </dl>
        </section>

        {/* Test metadata — sandbox parity. */}
        <section
          aria-labelledby="test-metadata-heading"
          className="rounded-lg border border-line bg-card p-6"
        >
          <Heading id="test-metadata-heading" level="h2" className="text-h3">
            Test metadata
          </Heading>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted-2">Test title</dt>
              <dd>{result.test.title}</dd>
            </div>
            <div>
              <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted-2">Category</dt>
              <dd>{result.test.category ?? '—'}</dd>
            </div>
            <div>
              <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted-2">Version</dt>
              <dd>{result.test.version ?? '—'}</dd>
            </div>
            <div>
              <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted-2">Total questions</dt>
              <dd className="font-mono">{result.total_questions}</dd>
            </div>
          </dl>
        </section>

        {/* Per-question review — the "exam paper handed back" surface. */}
        <section
          aria-labelledby="question-review-heading"
          className="rounded-lg border border-line bg-card p-6"
        >
          <Heading id="question-review-heading" level="h2" className="text-h3">
            Question review
          </Heading>
          <div className="mt-4 space-y-3">
            {result.per_question.map((question, index) => {
              const optionRows = buildOptionReviewRows(question);

              return (
                <article key={question.question_id} className="rounded-lg border border-line bg-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-mono text-eyebrow uppercase tracking-wide text-muted-2">
                        Question {index + 1}
                      </p>
                      <p className="mt-1 text-sm font-medium">
                        {question.question_text ?? question.question_id}
                      </p>
                    </div>
                    <Badge variant={question.is_correct ? 'success' : 'error'}>
                      {question.is_correct ? '✓ Correct' : '✗ Incorrect'}
                    </Badge>
                  </div>

                  {optionRows.length > 0 ? (
                    <ul className="mt-3 space-y-2" aria-label={`Options for question ${index + 1}`}>
                      {optionRows.map((option) => (
                        <li
                          key={option.id}
                          className="flex items-start gap-2 rounded-md border border-line px-3 py-2 text-sm"
                          style={optionStatusStyles(option.status)}
                        >
                          <span className="font-mono text-xs">{optionStatusLabel(option.status)}</span>
                          <span>{option.text}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-3 space-y-1 text-sm text-muted">
                      <p>User answer: {question.user_answer || '—'}</p>
                      <p>Correct answer: {question.correct_answer || '—'}</p>
                    </div>
                  )}

                  {question.explanation && (
                    <p className="mt-3 rounded-md bg-bg-warm p-3 text-sm text-ink">
                      Explanation: {question.explanation}
                    </p>
                  )}
                </article>
              );
            })}
          </div>
        </section>

        {/* Certificate of Achievement — conditional, only when passed.
            Per `docs/ia/public-result.md` § Minimum 10-11 + Polish 8/10.
            Even in embed mode this band keeps its accent border so the
            credential remains visually identifiable. */}
        {result.passed && (
          <section
            aria-labelledby="certificate-heading"
            className="rounded-lg border-2 border-accent bg-accent-l/30 p-6"
          >
            <Heading id="certificate-heading" level="h2" className="text-h3">
              Certificate of Achievement
            </Heading>
            <p className="mt-2 font-sans text-small text-muted">
              {candidateName} · {result.test.title} · {toLocaleTimestamp(result.completed_at)}
            </p>
            {result.certificate_id && (
              <p className="mt-3 font-mono text-body text-ink">
                {result.certificate_id}
              </p>
            )}
            <div className="mt-4">
              {result.certificate_download_url ? (
                <Link
                  href={result.certificate_download_url}
                  className="inline-flex rounded-md border border-line-2 px-4 py-2 text-small font-medium no-underline hover:bg-bg-warm"
                >
                  Download certificate
                </Link>
              ) : (
                <Button variant="secondary" type="button" className="inline-flex" disabled>
                  Download certificate (coming soon)
                </Button>
              )}
            </div>
          </section>
        )}
      </div>
    </PageFrame>
  );
}
